import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MonitoringMapComponent } from './components/monitoring-map/monitoring-map.component';
import { MapVehicle, STATUSES } from './cctv-monitoring-map.models';
import { Router } from '@angular/router';
import axiosJC261 from 'src/app/Service/axiosJC261';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import {
  ImageRecord,
  MonitoringImage,
} from '../cctv-monitoring/cctv-monitoring.models';
import { normalizeImage } from '../cctv-monitoring/cctv-monitoring.utils';
import { SignalrImageService } from 'src/app/Service/signalr-image.service';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { log } from 'geotiff/dist-node/logging';

@Component({
  selector: 'app-cctv-monitoring-map',
  providers: [SignalrImageService],
  templateUrl: './cctv-monitoring-map.component.html',
  styleUrls: ['./cctv-monitoring-map.component.css'],
})
export class CctvMonitoringMapComponent implements OnInit, OnDestroy {
  @ViewChild(MonitoringMapComponent) mapComponent?: MonitoringMapComponent;

  private destroyed = false;
  private destroy$ = new Subject<void>();
  private refreshTimer?: ReturnType<typeof setInterval>;
  private pendingHubImages: ImageRecord[] = [];
  readonly statuses = STATUSES;
  vehicles: MapVehicle[] = [];
  filteredVehicles: MapVehicle[] = [];
  search = '';
  status = 'all';
  category: 'cabin' | 'outside' = 'cabin';
  onlyImages = false;
  showThumbnails = true;
  clustersEnabled = true;
  selectedVehicle: MapVehicle | null = null;
  visibleImageCount = 0;
  mapUnavailable = !(window as any).strMapUrl;
  images: MonitoringImage[] = [];
  hasLoaded = false;
  loading = false;
  loadError = '';

  constructor(
    private guard: GurdServiceService,
    private notification: NotificationService,
    private otherService: OtherService,
    private router: Router,
    private imageHubService: SignalrImageService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.loadImages();
    if (environment.imageUrlHub?.trim()) {
      this.imageHubService.isHubConnected$
        .pipe(takeUntil(this.destroy$))
        .subscribe((connected) => {
          if (connected) {
            if (this.refreshTimer) {
              clearInterval(this.refreshTimer);
              this.refreshTimer = undefined;
              this.loadImages();
            }
          } else if (!this.refreshTimer) {
            this.refreshTimer = setInterval(() => this.loadImages(), 60000);
          }
        });
      this.imageHubService.hubError$
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          console.error('HC_HubError', data);
        });
      this.imageHubService.mobileImageReceived$
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: ImageRecord) => {
          this.zone.run(() => {
            if (this.loading) this.pendingHubImages.push(data);
            this.updateHubImage(data);
          });
        });
      this.imageHubService.init();
    } else {
      this.refreshTimer = setInterval(() => this.loadImages(), 60000);
    }
  }

  async loadImages(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.loadError = '';
    try {
      const response = await axiosJC261.post('GetLatestImages', {
        iSystemUser: Number(this.guard.decrypt(localStorage.getItem('ID'))),
        strSession: this.guard.decrypt(localStorage.getItem('Session')),
      });
      if (this.destroyed) return;
      const result =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;
      if (result?.ISSessionExpired === true) {
        this.notification.showError('نشست کاربری شما منقضی شده است');
        this.otherService.removeLocalStorage();
        this.router.navigate(['login'], { queryParams: { error: '1xzOPwsZ' } });
        return;
      }
      if (result?.iResult === 0)
        throw new Error(result.strError || 'دریافت تصاویر ناموفق بود');
      const rows = Array.isArray(result)
        ? result
        : result?.Data ||
          result?.data ||
          result?.Table ||
          result?.rows ||
          result?.images;
      if (!Array.isArray(rows))
        throw new Error('پاسخ دریافت تصاویر معتبر نیست');

      const baseUrl = new URL(
        axiosJC261.defaults.baseURL || '/',
        window.location.href,
      ).origin;
      const latest = new Map<string, MonitoringImage>();
      const vehicles = new Map<string, MapVehicle>();
      for (const row of rows as ImageRecord[]) {
        if (!row || typeof row !== 'object') continue;
        const id = String(row.strUnitID || row.strIMEI || '').trim();
        if (id) {
          const vehicle: MapVehicle = { ...row, strUnitID: id };
          const previous = vehicles.get(id);
          const valid = this.hasLocation(vehicle);
          const previousValid = previous && this.hasLocation(previous);
          if (
            !previous ||
            (valid && !previousValid) ||
            (valid === !!previousValid &&
              (Date.parse(vehicle.dtDateTime || '') || 0) >
                (Date.parse(previous.dtDateTime || '') || 0))
          )
            vehicles.set(id, vehicle);
        }
        const image = normalizeImage(row, baseUrl);
        if (!image) continue;
        const key = `${image.unitId}|${image.category}`;
        const previous = latest.get(key);
        if (
          !previous ||
          (Date.parse(image.date) || 0) > (Date.parse(previous.date) || 0)
        ) {
          latest.set(key, image);
        }
      }
      this.images = Array.from(latest.values());
      this.vehicles = Array.from(vehicles.values());
      this.pendingHubImages.forEach((record) =>
        this.updateHubImage(record, false),
      );
      this.pendingHubImages = [];
      this.hasLoaded = true;
      this.updateMarkers();
    } catch (error) {
      this.loadError =
        error instanceof Error && error.message !== 'Network Error'
          ? error.message
          : 'اتصال به سرور برقرار نشد';
      if (this.hasLoaded) this.notification.showError(this.loadError);
    } finally {
      this.pendingHubImages = [];
      this.loading = false;
    }
  }

  private updateHubImage(record: ImageRecord, refreshMarkers = true): void {
    if (!record || typeof record !== 'object') return;
    const id = String(record.strUnitID || record.strIMEI || '').trim();
    const index = this.vehicles.findIndex(
      (vehicle) => this.vehicleId(vehicle) === id,
    );
    if (index === -1) return;

    const previous = this.vehicles[index];
    const locationTime = Date.parse(record.dtDateTime || '') || 0;
    const previousLocationTime = Date.parse(previous.dtDateTime || '') || 0;
    if (!locationTime || locationTime >= previousLocationTime) {
      const vehicle: MapVehicle = { ...previous, ...record, strUnitID: id };
      if (!this.hasLocation(vehicle)) {
        vehicle.fLat = previous.fLat;
        vehicle.fLon = previous.fLon;
      }
      this.vehicles[index] = vehicle;
      if (this.selectedVehicle && this.vehicleId(this.selectedVehicle) === id)
        this.selectedVehicle = vehicle;
    }

    const baseUrl = new URL(
      axiosJC261.defaults.baseURL || '/',
      window.location.href,
    ).origin;
    const image = normalizeImage(record, baseUrl);
    if (image) {
      const imageIndex = this.images.findIndex(
        (item) => item.unitId === id && item.category === image.category,
      );
      const previousImage = this.images[imageIndex];
      if (
        !previousImage ||
        (Date.parse(image.date) || 0) >= (Date.parse(previousImage.date) || 0)
      ) {
        if (!record.strFullName)
          image.name = previousImage?.name || previous.strFullName || id;
        if (!record.strVehicleNo)
          image.plate = previousImage?.plate || previous.strVehicleNo || '';
        const images = [...this.images];
        if (imageIndex === -1) images.push(image);
        else images[imageIndex] = image;
        this.images = images;
      }
    }
    if (refreshMarkers) this.updateMarkers();
  }

  vehicleId(vehicle: MapVehicle): string {
    return String(vehicle.strUnitID || vehicle.strUnitId || '').trim();
  }

  imageFor(vehicle: MapVehicle): MonitoringImage | undefined {
    return this.images.find(
      (image) =>
        image.unitId === this.vehicleId(vehicle) &&
        image.category === this.category,
    );
  }

  private hasLocation(vehicle: MapVehicle): boolean {
    if (
      vehicle.fLat == null ||
      vehicle.fLon == null ||
      vehicle.fLat === '' ||
      vehicle.fLon === ''
    )
      return false;
    const lat = Number(vehicle.fLat),
      lon = Number(vehicle.fLon);
    return (
      Number.isFinite(lat) &&
      Number.isFinite(lon) &&
      lat >= -90 &&
      lat <= 90 &&
      lon >= -180 &&
      lon <= 180 &&
      !(lat === 0 && lon === 0)
    );
  }

  updateMarkers(): void {
    const query = this.search.trim().toLowerCase();
    this.filteredVehicles = this.vehicles.filter((vehicle) => {
      return (
        this.hasLocation(vehicle) &&
        (this.status === 'all' ||
          String(vehicle.tiDeviceStatus ?? 0) === this.status) &&
        (!query ||
          [
            vehicle.strFullName,
            vehicle.strVehicleNo,
            this.vehicleId(vehicle),
          ].some((value) =>
            String(value || '')
              .toLowerCase()
              .includes(query),
          )) &&
        (!this.onlyImages || !!this.imageFor(vehicle))
      );
    });
    this.visibleImageCount = this.filteredVehicles.filter(
      (vehicle) => !!this.imageFor(vehicle),
    ).length;
  }

  zoomToSelectedVehicle(): void {
    if (!this.selectedVehicle) return;
    this.mapComponent?.zoomToVehicle(this.selectedVehicle);
    this.selectedVehicle = null;
  }

  resetView(): void {
    this.mapComponent?.fitVehicles(this.filteredVehicles);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.imageHubService.disconnect();
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }
}
